// TokenUsageClient — real HTTP implementation.
//
// Route construction now lives in `config/api.ts`'s `tokenUsageEndpoints`
// (registered alongside every other `endpoints.*` entry) rather than a
// separate local `paths` object, and `authHeaders` is the shared helper used
// by every service client — both were duplicated here before (see PR #96968
// thread 974825). Absorbing a future contract change is still a small,
// contained diff: update `tokenUsageEndpoints` in config/api.ts; the
// `TokenUsageClient` interface and every caller (hooks, components) stay
// unchanged.
//
// Confirmed final contract (cross-checked against PR4 commit 16001124 and
// PR5 commit 57ec88ab's deployed bicep/policy XML):
//   GET /mytokenusage                     - current/historical own usage (?year=&month=)
//   GET /mytokenusage/periods             - available periods for own OID
//   GET /mytokenusage/admin/{userOid}     - admin: specific user (?year=&month=)
//   GET /mytokenusage/admin/org           - admin: org-wide aggregate (?year=&month=)
// Base URL already includes /tmds via VITE_API_BASE_URL - these are
// relative to that, same as the other `endpoints.*`.
//
// Note: the org-wide route was renamed from `/admin/all` to `/admin/org`
// by PR5 to fix an APIM route-ambiguity bug — the literal "all" segment
// collided with the `/admin/{userOid}` wildcard template, causing
// indeterminate operation routing. `/admin/{userOid}` is unaffected.
//
// Deliberately does NOT reuse the legacy (deprecated) `/tmds/tokenusage`
// path — see docs/change_plans/archive/token-visibility.md non-goals.
import { tokenUsageEndpoints, authHeaders, type TokenProvider } from '../config/api';
import { NotFoundError } from '../utils/errors';
import type {
  AvailablePeriod,
  AvailablePeriodsResult,
  TokenUsageDocument,
  UserUsageSummary,
} from '../types/tokenusage';

// Re-exported for back-compat with any existing `import type { TokenProvider }
// from './tokenusage'` consumers (e.g. tokenusage.factory.ts); the canonical
// declaration now lives in config/api.ts, shared with tmds.ts (see PR #96968
// thread 974825).
export type { TokenProvider };

/**
 * Interface every TokenUsageClient implementation (real HTTP + fixtures)
 * must satisfy. Keeping this stable is what lets the page/hooks/components
 * be written once and pointed at fixtures now, then the real API later.
 */
export interface TokenUsageClient {
  /** Current (in-progress) month for the caller's own OID. isFinal is always false. */
  getCurrentMonth(): Promise<TokenUsageDocument>;
  /** A specific historical or current period for the caller's own OID. Throws NotFoundError if no archive exists for that period. */
  getPeriod(year: number, month: number): Promise<TokenUsageDocument>;
  /** Periods available to query (for populating the month/year picker). */
  getAvailablePeriods(): Promise<AvailablePeriodsResult>;
  /** Admin only: a specific user's usage for a period (defaults to current month if omitted). Throws NotFoundError if no archive exists. */
  getUserPeriod(oid: string, year?: number, month?: number): Promise<TokenUsageDocument>;
  /** Admin only: org-wide usage (including the byUser map) for a period (defaults to current month if omitted). */
  getAllUsers(year?: number, month?: number): Promise<TokenUsageDocument>;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Current UTC period key ("yyyy-MM"), used to derive isFinal client-side for /mytokenusage/periods (see toAvailablePeriodsResult). */
function currentUtcPeriodKey(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${pad2(now.getUTCMonth() + 1)}`;
}

/**
 * Raw response shape from GET /mytokenusage/periods (PR4's
 * list-my-token-usage-periods.xml) — an object with a `periods` array of
 * plain "yyyy-MM" strings, NOT an AvailablePeriod[], plus a `partial` flag.
 * The API does not return a per-period isFinal flag, so it's derived
 * client-side: the current UTC month (if present in the list at all) is
 * isFinal: false, every other returned period is isFinal: true.
 *
 * `partial` is true if one or more of the fixed per-month storage probes
 * backing this endpoint returned neither 200 nor 404 (a genuine
 * storage/MI/throttle error rather than real absence) — meaning `periods`
 * may be missing entries that do in fact exist. This must be surfaced to
 * the caller, not discarded (see PR #96968 thread 974824).
 */
interface RawAvailablePeriodsResponse {
  userOid: string;
  windowMonths: number;
  periods: string[];
  partial: boolean;
}

function toAvailablePeriodsResult(raw: RawAvailablePeriodsResponse): AvailablePeriodsResult {
  const currentKey = currentUtcPeriodKey();
  const periods: AvailablePeriod[] = (raw.periods ?? []).map((period) => {
    const [yearStr, monthStr] = period.split('-');
    return {
      year: Number(yearStr),
      month: Number(monthStr),
      period,
      isFinal: period !== currentKey,
    };
  });
  return { periods, partial: !!raw.partial };
}

async function toJson<T>(res: Response): Promise<T> {
  if (res.status === 404) {
    throw new NotFoundError('No archived usage data found for the requested period.');
  }
  if (!res.ok) {
    // Do not read or log the raw backend response body: it may contain
    // internal diagnostics (stack traces, connection strings, tokens) not
    // intended for end users or for application logs (SFI PS 3.1). Log only
    // the status plus a best-effort request correlation id, and throw a
    // generic, sanitized message to the UI.
    const requestId = res.headers.get('apim-request-id') ?? res.headers.get('x-ms-request-id') ?? 'unknown';
    console.error(`Token usage request failed (HTTP ${res.status} ${res.statusText}, request-id: ${requestId})`);
    throw new Error(`Request failed (HTTP ${res.status} ${res.statusText}). Please try again or contact support.`);
  }
  return (await res.json()) as T;
}

export function createTokenUsageClient(getToken: TokenProvider): TokenUsageClient {
  return {
    async getCurrentMonth(): Promise<TokenUsageDocument> {
      const res = await fetch(tokenUsageEndpoints.current(), {
        method: 'GET',
        headers: await authHeaders(getToken),
      });
      return toJson<TokenUsageDocument>(res);
    },

    async getPeriod(year: number, month: number): Promise<TokenUsageDocument> {
      const res = await fetch(tokenUsageEndpoints.historical(year, month), {
        method: 'GET',
        headers: await authHeaders(getToken),
      });
      return toJson<TokenUsageDocument>(res);
    },

    async getAvailablePeriods(): Promise<AvailablePeriodsResult> {
      const res = await fetch(tokenUsageEndpoints.periods(), {
        method: 'GET',
        headers: await authHeaders(getToken),
      });
      const raw = await toJson<RawAvailablePeriodsResponse>(res);
      return toAvailablePeriodsResult(raw);
    },

    async getUserPeriod(oid: string, year?: number, month?: number): Promise<TokenUsageDocument> {
      const res = await fetch(tokenUsageEndpoints.adminUser(oid, year, month), {
        method: 'GET',
        headers: await authHeaders(getToken),
      });
      return toJson<TokenUsageDocument>(res);
    },

    async getAllUsers(year?: number, month?: number): Promise<TokenUsageDocument> {
      const res = await fetch(tokenUsageEndpoints.adminAll(year, month), {
        method: 'GET',
        headers: await authHeaders(getToken),
      });
      return toJson<TokenUsageDocument>(res);
    },
  };
}

/** Derive a sorted (desc by grandTotal) admin "all users" summary list from an org-wide document's byUser map. */
export function summarizeByUser(doc: TokenUsageDocument): UserUsageSummary[] {
  const byUser = doc.byUser ?? {};
  return Object.entries(byUser)
    .map(([userOid, breakdown]) => ({ userOid, grandTotal: breakdown.grandTotal }))
    .sort((a, b) => b.grandTotal.totalTokens - a.grandTotal.totalTokens);
}
